/**
 * CoffeeMates Knowledge Sessions Service
 */

import httpClient from '../utils/httpClient';

export interface KnowledgeSession {
  id: string;
  title: string;
  description: string;
  topic_tags: string[];
  event_type: 'knowledge_session';
  knowledge_fee: number;
  voucher_tier: string;
  min_capacity: number;
  max_capacity: number;
  current_count: number;
  status: string;
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes: number;
  zone_id: string;
  venue_id: string | null;
  venue_name?: string;
  venue_address?: string;
  created_by: string;
  professional_name?: string;
  professional_avatar?: string;
  professional_rating?: number;
  professional_sessions_count?: number;
  avg_rating: number | null;
  rating_count: number;
  signup_deadline: string;
  created_at: string;
}

export interface Eligibility {
  eligible: boolean;
  events_attended: number;
  avg_rating: number | null;
  reasons: string[];
}

export interface ProfessionalProfile {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  bio: string | null;
  industry: string | null;
  sessions_created: number;
  avg_session_rating: number | null;
  total_students_taught: number;
  sessions: KnowledgeSession[];
}

export interface CreateSessionData {
  title: string;
  description: string;
  topic_tags: string[];
  knowledge_fee: number;
  min_capacity: number;
  max_capacity: number;
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes?: number;
  zone_id: string;
  venue_id?: string;
  voucher_tier?: string;
}

export interface RateStudentData {
  student_user_id: string;
  preparedness: number;
  engagement: number;
  punctuality: number;
  comment?: string;
}

const knowledgeService = {
  async checkEligibility(): Promise<Eligibility> {
    const { data } = await httpClient.get<Eligibility>('/knowledge/eligibility');
    return data;
  },

  async create(session: CreateSessionData): Promise<KnowledgeSession> {
    const { data } = await httpClient.post<KnowledgeSession>('/knowledge/create', session);
    return data;
  },

  async browse(params?: { zone_id?: string; topic?: string }): Promise<KnowledgeSession[]> {
    const query = new URLSearchParams();
    if (params?.zone_id) query.set('zone_id', params.zone_id);
    if (params?.topic) query.set('topic', params.topic);
    const qs = query.toString();
    const { data } = await httpClient.get<KnowledgeSession[]>(`/knowledge/browse${qs ? '?' + qs : ''}`);
    return data;
  },

  async getMySessions(): Promise<KnowledgeSession[]> {
    const { data } = await httpClient.get<KnowledgeSession[]>('/knowledge/my');
    return data;
  },

  async getProfessionalProfile(userId: string): Promise<ProfessionalProfile> {
    const { data } = await httpClient.get<ProfessionalProfile>(`/knowledge/professional/${userId}`);
    return data;
  },

  async update(eventId: string, updates: Partial<CreateSessionData>): Promise<unknown> {
    const { data } = await httpClient.put(`/knowledge/${eventId}`, updates);
    return data;
  },

  async cancel(eventId: string): Promise<unknown> {
    const { data } = await httpClient.del(`/knowledge/${eventId}`);
    return data;
  },

  async accept(eventId: string): Promise<unknown> {
    const { data } = await httpClient.post(`/knowledge/${eventId}/accept`);
    return data;
  },

  async rateStudent(eventId: string, rating: RateStudentData): Promise<unknown> {
    const { data } = await httpClient.post(`/knowledge/${eventId}/rate-student`, rating);
    return data;
  },
};

export default knowledgeService;
